"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { SlideUpPage } from "@/components/layout/SlideUpPage";
import type { Company } from "@/types";

interface EditCompanyFormProps {
  company: Company;
  onClose: () => void;
  onSuccess: () => void;
}

const INDUSTRIES = [
  "IT og Teknologi",
  "Bygg og Anlegg",
  "Helse og Omsorg",
  "Service og Handel",
  "Industri og Produksjon",
  "Transport og Logistikk",
  "Utdanning",
  "Finans",
  "Media og Kommunikasjon",
  "Annet",
];

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "500+"];

export function EditCompanyForm({ company, onClose, onSuccess }: EditCompanyFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: company.name,
    description: company.description,
    industry: company.industry,
    size: company.size,
    location: company.location,
    website: company.website || "",
    logo: company.logo || "",
    address: company.address || "",
    linkedIn: company.linkedIn || "",
    contactEmail: company.contactEmail || "",
    contactPhone: company.contactPhone || "",
    values: company.values?.join(", ") || "",
    benefits: company.benefits?.join(", ") || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      // TODO: Replace with actual Convex mutation when ready
      // const result = await updateCompany({
      //   companyId: company._id,
      //   ...formData,
      //   values: formData.values.split(",").map(v => v.trim()).filter(Boolean),
      //   benefits: formData.benefits.split(",").map(b => b.trim()).filter(Boolean),
      // });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Company updated:", formData);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Kunne ikke oppdatere firma");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SlideUpPage onClose={onClose} showCloseButton={true}>
      <div className="min-h-full p-6" style={{ backgroundColor: "var(--color-bg-primary)" }}>
        {/* Header */}
        <div className="mb-6 pt-10">
          <h2
            style={{
              fontSize: "var(--font-size-title-md)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--color-text-primary)",
            }}
          >
            Rediger firmaprofil
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3
              style={{
                fontSize: "var(--font-size-body-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-primary)",
              }}
            >
              Grunnleggende info
            </h3>

            <div>
              <label
                htmlFor="name"
                className="mb-1.5 block text-sm"
                style={{ color: "var(--color-text-primary)" }}
              >
                Firmanavn *
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="mb-1.5 block text-sm"
                style={{ color: "var(--color-text-primary)" }}
              >
                Beskrivelse *
              </label>
              <textarea
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="industry"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Bransje *
                </label>
                <select
                  id="industry"
                  required
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                >
                  {INDUSTRIES.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="size"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Størrelse *
                </label>
                <select
                  id="size"
                  required
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                >
                  {COMPANY_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} ansatte
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="location"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Lokasjon *
                </label>
                <input
                  type="text"
                  id="location"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="address"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Adresse
                </label>
                <input
                  type="text"
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Contact & Social */}
          <div className="space-y-4 border-t pt-4" style={{ borderColor: "var(--color-border)" }}>
            <h3
              style={{
                fontSize: "var(--font-size-body-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-primary)",
              }}
            >
              Kontakt & sosiale medier
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="website"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Nettside
                </label>
                <input
                  type="url"
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="linkedIn"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  LinkedIn
                </label>
                <input
                  type="url"
                  id="linkedIn"
                  value={formData.linkedIn}
                  onChange={(e) => setFormData({ ...formData, linkedIn: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                  placeholder="https://linkedin.com/company/..."
                />
              </div>

              <div>
                <label
                  htmlFor="contactEmail"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Kontakt-epost
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                  placeholder="kontakt@firma.no"
                />
              </div>

              <div>
                <label
                  htmlFor="contactPhone"
                  className="mb-1.5 block text-sm"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  Telefon
                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full rounded-lg border px-4 py-2.5"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text-primary)",
                  }}
                  placeholder="+47 123 45 678"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="logo"
                className="mb-1.5 block text-sm"
                style={{ color: "var(--color-text-primary)" }}
              >
                Logo URL
              </label>
              <input
                type="url"
                id="logo"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                className="w-full rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>

          {/* Culture & Values */}
          <div className="space-y-4 border-t pt-4" style={{ borderColor: "var(--color-border)" }}>
            <h3
              style={{
                fontSize: "var(--font-size-body-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-primary)",
              }}
            >
              Kultur & verdier
            </h3>

            <div>
              <label
                htmlFor="values"
                className="mb-1.5 block text-sm"
                style={{ color: "var(--color-text-primary)" }}
              >
                Verdier (kommaseparert)
              </label>
              <input
                type="text"
                id="values"
                value={formData.values}
                onChange={(e) => setFormData({ ...formData, values: e.target.value })}
                className="w-full rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
                placeholder="Innovasjon, Samarbeid, Bærekraft"
              />
            </div>

            <div>
              <label
                htmlFor="benefits"
                className="mb-1.5 block text-sm"
                style={{ color: "var(--color-text-primary)" }}
              >
                Fordeler (kommaseparert)
              </label>
              <input
                type="text"
                id="benefits"
                value={formData.benefits}
                onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                className="w-full rounded-lg border px-4 py-2.5"
                style={{
                  backgroundColor: "var(--color-bg-secondary)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
                placeholder="Fleksibel arbeidstid, Hjemmekontor, Gratis lunsj"
              />
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div
              className="rounded-lg p-3 text-sm"
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
              }}
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold"
              style={{
                backgroundColor: "var(--color-bg-secondary)",
                color: "var(--color-text-primary)",
              }}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-lg py-3 font-semibold"
              style={{
                backgroundColor: "var(--color-bg-accent)",
                color: "var(--color-text-primary)",
              }}
            >
              {isSubmitting ? "Lagrer..." : "Lagre endringer"}
            </Button>
          </div>
        </form>
      </div>
    </SlideUpPage>
  );
}

